import { useState, useEffect, useCallback } from 'react';
import { 
  RefreshCw, 
  Cloud, 
  CheckCircle, 
  AlertCircle, 
  Clock,
  WifiOff,
  ArrowUpCircle,
  ArrowDownCircle,
  Settings,
  Activity
} from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import {
  fetchSyncStatus,
  triggerSync,
  fetchSyncHistory,
  type SyncStatus as SyncStatusResponse,
  type SyncHistoryItem,
} from '../services/sync-service';
import { fetchSyncSettings, updateSyncSettings } from '../services/sync-share-service';

interface SyncCategory {
  id: string;
  name: string;
  lastSync: Date;
  status: 'synced' | 'pending' | 'syncing' | 'error';
  items: number;
  direction: 'upload' | 'download' | 'bidirectional';
}

export default function RealtimeSync() {
  const [syncCategories, setSyncCategories] = useState<SyncCategory[]>([]);
  const [syncHistory, setSyncHistory] = useState<SyncHistoryItem[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastGlobalSync, setLastGlobalSync] = useState<Date | null>(null);
  const [syncStatusResponse, setSyncStatusResponse] = useState<SyncStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncSettings, setSyncSettings] = useState({
    autoSync: true,
    syncInterval: 5,
    wifiOnly: false,
    syncOnStartup: true,
    conflictResolution: 'server-wins' as 'server-wins' | 'client-wins' | 'manual',
  });

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const [statusResult, historyResult, settingsResult] = await Promise.allSettled([
        fetchSyncStatus(),
        fetchSyncHistory(),
        fetchSyncSettings(),
      ]);

      if (statusResult.status === 'fulfilled') {
        const status = statusResult.value;
        setSyncStatusResponse(status);
        setLastGlobalSync(status.lastSync ? new Date(status.lastSync) : null);

        const pendingItems = status.pendingItems ?? 0;
        setSyncCategories([
          { id: '1', name: 'Tabs', lastSync: status.lastSync ? new Date(status.lastSync) : new Date(), status: status.status, items: 0, direction: 'bidirectional' },
          { id: '2', name: 'Sessions', lastSync: status.lastSync ? new Date(status.lastSync) : new Date(), status: status.status, items: 0, direction: 'bidirectional' },
          { id: '3', name: 'Bookmarks', lastSync: status.lastSync ? new Date(status.lastSync) : new Date(), status: status.status, items: 0, direction: 'bidirectional' },
          { id: '4', name: 'Pending Items', lastSync: new Date(), status: pendingItems > 0 ? 'pending' : 'synced', items: pendingItems, direction: 'upload' },
        ]);
      } else {
        setError('Failed to load sync status');
      }

      if (historyResult.status === 'fulfilled') {
        setSyncHistory(historyResult.value);
      }

      if (settingsResult.status === 'fulfilled') {
        const s = settingsResult.value;
        setSyncSettings({
          autoSync: s.auto_sync,
          syncInterval: s.sync_interval,
          wifiOnly: s.wifi_only,
          syncOnStartup: s.sync_on_startup,
          conflictResolution: (s.conflict_resolution as 'server-wins' | 'client-wins' | 'manual') || 'server-wins',
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sync data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const persistSettings = async (updates: Partial<typeof syncSettings>) => {
    const next = { ...syncSettings, ...updates };
    setSyncSettings(next);
    try {
      await updateSyncSettings({
        auto_sync: next.autoSync,
        sync_interval: next.syncInterval,
        wifi_only: next.wifiOnly,
        sync_on_startup: next.syncOnStartup,
        conflict_resolution: next.conflictResolution,
      });
    } catch (err) {
      console.error('Failed to save sync settings:', err);
    }
  };

  const handleTriggerSync = async () => {
    setIsSyncing(true);
    setSyncCategories(prev => prev.map(s => 
      s.status === 'pending' ? { ...s, status: 'syncing' as const } : s
    ));
    try {
      await triggerSync();
      await loadData();
    } catch (err) {
      console.error('Sync failed:', err);
      setError(err instanceof Error ? err.message : 'Sync failed');
      setSyncCategories(prev => prev.map(s => 
        s.status === 'syncing' ? { ...s, status: 'error' as const } : s
      ));
    } finally {
      setIsSyncing(false);
    }
  };

  const getTimeSinceSync = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const getStatusColor = (status: SyncCategory['status']) => {
    switch (status) {
      case 'synced': return 'text-green-500';
      case 'pending': return 'text-yellow-500';
      case 'syncing': return 'text-blue-500';
      case 'error': return 'text-red-500';
    }
  };

  const getStatusBg = (status: SyncCategory['status']) => {
    switch (status) {
      case 'synced': return 'bg-success-500/10';
      case 'pending': return 'bg-warning-500/10';
      case 'syncing': return 'bg-info-500/10';
      case 'error': return 'bg-error-500/10';
    }
  };

  return (
    <div className="p-6 space-y-6 bg-khiimori-bg-elevated/5 backdrop-blur-xl border border-khiimori-border/10 rounded-xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-khiimori-text-primary">Real-time Sync</h1>
          <p className="text-sm text-khiimori-text-muted mt-1">
            Monitor and manage data synchronization across devices
          </p>
        </div>
        <Button
          onClick={handleTriggerSync}
          disabled={isSyncing || isLoading}
          aria-label="Sync Now"
        >
          <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
          {isSyncing ? 'Syncing...' : isLoading ? 'Loading...' : 'Sync Now'}
        </Button>
      </div>

      {error && (
        <div className="p-4 bg-error-500/20 border border-error-500/30 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-error-400" />
            <span className="text-error-400 text-sm">{error}</span>
          </div>
          <Button onClick={loadData} variant="ghost" size="sm" aria-label="Retry">
            Retry
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sync Summary */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-info-500/10 rounded-lg">
              <Cloud className="w-5 h-5 text-info-400" />
            </div>
            <div>
              <h2 className="font-semibold text-khiimori-text-primary">Status</h2>
              <p className="text-sm text-khiimori-text-muted">Last global sync</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="p-4 bg-khiimori-bg-elevated/5 rounded-lg text-center">
              <p className="text-sm text-khiimori-text-muted mb-1">Last Sync</p>
              <p className="text-xl font-bold text-khiimori-text-primary">
                {lastGlobalSync ? lastGlobalSync.toLocaleTimeString() : 'Never'}
              </p>
              <p className="text-xs text-khiimori-text-muted mt-1">
                {lastGlobalSync ? getTimeSinceSync(lastGlobalSync) : 'No sync recorded'}
              </p>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div className="p-3 bg-success-500/10 rounded-lg text-center">
                <p className="text-2xl font-bold text-success-400">
                  {syncCategories.filter(s => s.status === 'synced').length}
                </p>
                <p className="text-xs text-khiimori-text-muted">Synced</p>
              </div>
              <div className="p-3 bg-warning-500/10 rounded-lg text-center">
                <p className="text-2xl font-bold text-warning-400">
                  {syncCategories.filter(s => s.status === 'pending').length}
                </p>
                <p className="text-xs text-khiimori-text-muted">Pending</p>
              </div>
              <div className="p-3 bg-error-500/10 rounded-lg text-center">
                <p className="text-2xl font-bold text-error-400">
                  {syncCategories.filter(s => s.status === 'error').length}
                </p>
                <p className="text-xs text-khiimori-text-muted">Error</p>
              </div>
            </div>

            <div className="flex items-center justify-center gap-2 text-success-400">
              <Activity className="w-4 h-4" />
              <span className="text-sm">
                {syncStatusResponse?.status === 'synced' ? 'All systems operational' : 'Sync pending'}
              </span>
            </div>
          </div>
        </Card>

        {/* Sync Settings */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-primary-500/10 rounded-lg">
              <Settings className="w-5 h-5 text-primary-400" />
            </div>
            <div>
              <h2 className="font-semibold text-khiimori-text-primary">Settings</h2>
              <p className="text-sm text-khiimori-text-muted">Sync preferences</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-khiimori-bg-elevated/5 rounded-lg">
              <div className="flex items-center gap-3">
                <Cloud className="w-5 h-5 text-khiimori-text-muted" />
                <span className="text-sm font-medium text-khiimori-text-primary">Auto Sync</span>
              </div>
              <button
                onClick={() => persistSettings({ autoSync: !syncSettings.autoSync })}
                className={`w-12 h-6 rounded-full transition-colors ${
                  syncSettings.autoSync ? 'bg-primary-500' : 'bg-khiimori-bg-tertiary'
                }`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                  syncSettings.autoSync ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </button>
            </div>

            <div className="flex items-center justify-between p-3 bg-khiimori-bg-elevated/5 rounded-lg">
              <div className="flex items-center gap-3">
                <WifiOff className="w-5 h-5 text-khiimori-text-muted" />
                <span className="text-sm font-medium text-khiimori-text-primary">WiFi Only</span>
              </div>
              <button
                onClick={() => persistSettings({ wifiOnly: !syncSettings.wifiOnly })}
                className={`w-12 h-6 rounded-full transition-colors ${
                  syncSettings.wifiOnly ? 'bg-primary-500' : 'bg-khiimori-bg-tertiary'
                }`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                  syncSettings.wifiOnly ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </button>
            </div>

            <div className="flex items-center justify-between p-3 bg-khiimori-bg-elevated/5 rounded-lg">
              <div className="flex items-center gap-3">
                <RefreshCw className="w-5 h-5 text-khiimori-text-muted" />
                <span className="text-sm font-medium text-khiimori-text-primary">Sync on Startup</span>
              </div>
              <button
                onClick={() => persistSettings({ syncOnStartup: !syncSettings.syncOnStartup })}
                className={`w-12 h-6 rounded-full transition-colors ${
                  syncSettings.syncOnStartup ? 'bg-primary-500' : 'bg-khiimori-bg-tertiary'
                }`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                  syncSettings.syncOnStartup ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-khiimori-text-secondary mb-2">
                Sync Interval (minutes)
              </label>
              <Input
                type="number"
                value={syncSettings.syncInterval}
                onChange={(e) => persistSettings({ syncInterval: parseInt(e.target.value) || 5 })}
                className="w-full"
                min="1"
                max="60"
              />
            </div>
          </div>
        </Card>

        {/* Activity Log */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-success-500/10 rounded-lg">
              <Activity className="w-5 h-5 text-success-400" />
            </div>
            <div>
              <h2 className="font-semibold text-khiimori-text-primary">Recent Activity</h2>
              <p className="text-sm text-khiimori-text-muted">Sync history</p>
            </div>
          </div>
          
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {isLoading ? (
              <div className="text-center py-4">
                <RefreshCw className="w-5 h-5 animate-spin mx-auto text-khiimori-text-muted" />
                <p className="text-xs text-khiimori-text-muted mt-2">Loading history...</p>
              </div>
            ) : syncHistory.length === 0 ? (
              <div className="text-center py-4">
                <Clock className="w-5 h-5 mx-auto text-khiimori-text-muted" />
                <p className="text-xs text-khiimori-text-muted mt-2">No sync history yet</p>
              </div>
            ) : (
              syncHistory.map((log) => (
                <div key={log.id} className="flex items-center gap-3 p-3 bg-khiimori-bg-elevated/5 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-khiimori-text-primary truncate">{log.action}</p>
                    <p className="text-xs text-khiimori-text-muted">{log.device} - {log.tabsAffected} items</p>
                  </div>
                  <span className="text-xs text-khiimori-text-muted flex-shrink-0">
                    {log.timestamp ? new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                  </span>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>

      {/* Sync Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {syncCategories.map(category => (
          <Card key={category.id} className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                {category.direction === 'upload' && <ArrowUpCircle className="w-5 h-5 text-blue-500" />}
                {category.direction === 'download' && <ArrowDownCircle className="w-5 h-5 text-green-500" />}
                {category.direction === 'bidirectional' && (
                  <div className="flex">
                    <ArrowUpCircle className="w-5 h-5 text-blue-500 -mr-2" />
                    <ArrowDownCircle className="w-5 h-5 text-green-500" />
                  </div>
                )}
                <h3 className="font-semibold text-khiimori-text-primary">{category.name}</h3>
              </div>
              <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBg(category.status)} ${getStatusColor(category.status)}`}>
                {category.status}
              </span>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-khiimori-text-muted">Items</span>
                <span className="font-medium text-khiimori-text-primary">{category.items}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-khiimori-text-muted">Last Sync</span>
                <span className="text-khiimori-text-primary">{getTimeSinceSync(category.lastSync)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-khiimori-text-muted">Direction</span>
                <span className="text-khiimori-text-primary capitalize">{category.direction}</span>
              </div>
            </div>
            
            {category.status === 'error' && (
              <Button onClick={handleTriggerSync} variant="danger" className="mt-4 w-full" aria-label="Retry sync">
                <AlertCircle className="w-4 h-4" />
                Retry Sync
              </Button>
            )}
          </Card>
        ))}
      </div>

      {/* Conflict Resolution */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-warning-500/10 rounded-lg">
              <AlertCircle className="w-5 h-5 text-warning-400" />
            </div>
            <div>
              <h2 className="font-semibold text-khiimori-text-primary">Conflict Resolution</h2>
              <p className="text-sm text-khiimori-text-muted">How to handle data conflicts</p>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => persistSettings({ conflictResolution: 'server-wins' })}
            className={`p-4 rounded-lg border-2 transition-colors ${
              syncSettings.conflictResolution === 'server-wins'
                ? 'border-primary-500 bg-primary-500/10'
                : 'border-khiimori-border/10 hover:border-khiimori-border'
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <Cloud className="w-5 h-5 text-primary-400" />
              <span className="font-medium text-khiimori-text-primary">Server Wins</span>
            </div>
            <p className="text-xs text-khiimori-text-muted">
              Server version always takes precedence
            </p>
          </button>

          <button
            onClick={() => persistSettings({ conflictResolution: 'client-wins' })}
            className={`p-4 rounded-lg border-2 transition-colors ${
              syncSettings.conflictResolution === 'client-wins'
                ? 'border-primary-500 bg-primary-500/10'
                : 'border-khiimori-border/10 hover:border-khiimori-border'
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <Settings className="w-5 h-5 text-primary-400" />
              <span className="font-medium text-khiimori-text-primary">Client Wins</span>
            </div>
            <p className="text-xs text-khiimori-text-muted">
              Local version always takes precedence
            </p>
          </button>

          <button
            onClick={() => persistSettings({ conflictResolution: 'manual' })}
            className={`p-4 rounded-lg border-2 transition-colors ${
              syncSettings.conflictResolution === 'manual'
                ? 'border-primary-500 bg-primary-500/10'
                : 'border-khiimori-border/10 hover:border-khiimori-border'
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-primary-400" />
              <span className="font-medium text-khiimori-text-primary">Ask Each Time</span>
            </div>
            <p className="text-xs text-khiimori-text-muted">
              Choose for each conflict manually
            </p>
          </button>
        </div>
      </Card>
    </div>
  );
}